import { createTheme, Theme } from '@mui/material/styles';

// Returns focus-visible ring styles using the live theme palette so dynamic
// primary color overrides from ThemeContext are tracked correctly.
// Uses primary.main on light backgrounds; on dark (sidebar) backgrounds
// SidebarNavigation overrides this with a white ring directly in sx.
// Exported so components that aren't ButtonBase (and so miss the global override
// below) use this one definition instead of re-declaring the ring — pass a
// negative `outlineOffset` to inset it, e.g. a full-width table row.
export const focusVisibleStyles = (theme: Theme, outlineOffset: string = '3px') => ({
  outline: `2px solid ${theme.palette.primary.main}`,
  outlineOffset,
  borderRadius: '2px',
});

// GovIQ Color Palette — deep navy primary matching goviq.us branding
const a3Colors = {
  primary: {
    50: '#e8eaf6',
    100: '#c5cae9',
    200: '#9fa8da',
    300: '#7986cb',
    400: '#5c6bc0',
    500: '#1a237e', // Main primary — GovIQ deep navy
    600: '#1a237e',
    700: '#151c66',
    800: '#11174f',
    900: '#0d1138',
  },
  secondary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#1565c0', // Supporting blue — secondary accent
    600: '#1565c0',
    700: '#0d47a1',
    800: '#0a3880',
    900: '#072960',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#4CAF50',
    500: '#2E7D32', // WCAG AA green — matches A3
    600: '#2E7D32',
    700: '#1B5E20',
    800: '#1B5E20',
    900: '#0f3d0f',
  },
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#C2710C', // WCAG AA amber — matches A3
    600: '#C2710C',
    700: '#92400E',
    800: '#78350F',
    900: '#5a280a',
  },
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#EF5350',
    500: '#C62828', // WCAG AA red — matches A3
    600: '#C62828',
    700: '#7F1D1D',
    800: '#7F1D1D',
    900: '#5a1212',
  },
};

// A3 Platform Typography Configuration
const a3Typography = {
  fontFamily: '"Calibri", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '2.125rem',
    fontWeight: 300,
    lineHeight: 1.167,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '1.5rem',
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '1.25rem',
    fontWeight: 400,
    lineHeight: 1.167,
    letterSpacing: '0em',
  },
  h4: {
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: 1.235,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.334,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none' as const,
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
};

// A3 Platform Spacing Configuration
const a3Spacing = 8; // Base spacing unit (8px)

// Create A3 Platform Theme
export const createA3Theme = (): Theme => {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        ...a3Colors.primary,
        main: a3Colors.primary[500],
      },
      secondary: {
        ...a3Colors.secondary,
        main: a3Colors.secondary[500],
      },
      error: {
        ...a3Colors.error,
        main: a3Colors.error[500],
        light: a3Colors.error[400],
        dark: a3Colors.error[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        ...a3Colors.warning,
        main: a3Colors.warning[500],
        light: a3Colors.warning[400],
        dark: a3Colors.warning[700],
        contrastText: '#FFFFFF',
      },
      success: {
        ...a3Colors.success,
        main: a3Colors.success[500],
        light: a3Colors.success[400],
        dark: a3Colors.success[700],
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#0369A1',
        light: '#0EA5E9',
        dark: '#0C4A6E',
        contrastText: '#FFFFFF',
      },
      grey: a3Colors.grey,
      background: {
        default: '#f8f9fa',
        paper: '#ffffff',
      },
      text: {
        primary: 'rgba(0, 0, 0, 0.9)',
        secondary: 'rgba(0, 0, 0, 0.7)',
        disabled: 'rgba(0, 0, 0, 0.38)',
      },
    },
    typography: a3Typography,
    spacing: a3Spacing,
    shape: {
      borderRadius: 4,
    },
    components: {
      // Focus-visible — WCAG 2.2 AA keyboard focus rings
      MuiButtonBase: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            '&:focus-visible': focusVisibleStyles(theme),
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          // root wraps the focusable trigger; `select` slot has tabindex="-1"
          root: ({ theme }: { theme: Theme }) => ({
            '&:focus-visible': focusVisibleStyles(theme),
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }: { theme: Theme }) => ({
            '&:focus-visible': focusVisibleStyles(theme),
          }),
        },
      },
      // IconButton — 44px touch target (WCAG 2.5.8)
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: '44px',
            minHeight: '44px',
          },
        },
      },
      // Button Component Overrides
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 4,
            padding: '6px 16px',
            minWidth: 64,
            minHeight: '44px',
            '&:hover': {
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
            },
          },
          contained: {
            boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
            '&:hover': {
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
            },
          },
          outlined: {
            borderWidth: 1,
            '&:hover': {
              borderWidth: 1,
            },
          },
        },
      },
      // TextField Component Overrides
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
              minHeight: '44px',
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderWidth: 2,
              },
            },
          },
        },
      },
      // Paper Component Overrides
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          },
          elevation1: {
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
          },
          elevation2: {
            boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
          },
          elevation4: {
            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          },
        },
      },
      // Card Component Overrides
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)',
          },
        },
      },
      // Table Component Overrides
      MuiTable: {
        styleOverrides: {
          root: {
            fontFamily: '"Calibri", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: '#f5f5f5',
            '& .MuiTableCell-head': {
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'rgba(0, 0, 0, 0.9)',
              borderBottom: '2px solid #e0e0e0',
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root': {
              // Hover is scoped to MUI's own opt-in `hover` prop. Applying it to every body row
              // offered a false affordance on rows that do nothing, and forced each table to claw
              // it back locally. Two classes plus :hover also outranks the zebra rule below, which
              // an unscoped `&:hover` did not — so even rows had no hover feedback at all.
              '&.MuiTableRow-hover:hover': {
                backgroundColor: '#f8f9fa',
              },
              '&:nth-of-type(even)': {
                backgroundColor: '#fafafa',
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: '"Calibri", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '0.875rem',
            padding: '8px 16px',
            color: 'rgba(0, 0, 0, 0.9)',
            borderBottom: '1px solid #e0e0e0',
          },
          head: {
            fontWeight: 600,
            color: 'rgba(0, 0, 0, 0.9)',
          },
        },
      },
      // AppBar Component Overrides
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          },
        },
      },
      // Chip Component Overrides — square chips match A3 / government aesthetic
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 24,
          },
        },
      },
      // Dialog Component Overrides
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
          },
        },
      },
      // Snackbar Component Overrides
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiSnackbarContent-root': {
              borderRadius: 4,
            },
          },
        },
      },
      // Tooltip Component Overrides
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 4,
            fontSize: '0.75rem',
          },
        },
      },
      // FormControl Component Overrides
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginBottom: 16,
          },
        },
      },
      // FormLabel Component Overrides
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: 8,
          },
        },
      },
      // Grid Component Overrides
      MuiGrid: {
        styleOverrides: {
          container: {
            '&.MuiGrid-spacing-xs-2 > .MuiGrid-item': {
              padding: 8,
            },
            '&.MuiGrid-spacing-xs-3 > .MuiGrid-item': {
              padding: 12,
            },
            '&.MuiGrid-spacing-xs-4 > .MuiGrid-item': {
              padding: 16,
            },
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });
};

// Export the default A3 theme
export const a3Theme = createA3Theme();

// Export theme utilities for styled components
export const getA3Color = (color: keyof typeof a3Colors, shade: number = 500) => {
  return a3Colors[color][shade as keyof typeof a3Colors[typeof color]];
};

export const getA3Spacing = (multiplier: number) => {
  return a3Spacing * multiplier;
};